import React, { useMemo, useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Container
} from "reactstrap";
import { Link } from "react-router-dom";
import './ServiciosAdicionales.css';

/**
 * Componente: ServiciosAdicionales - Diseño Moderno y Minimalista con Animaciones
 */
const ServiciosAdicionales = ({ data, loading }) => {
  const [selectedOt, setSelectedOt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedItems, setAnimatedItems] = useState([]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (data && data.por_servicio) {
      const timer = setTimeout(() => {
        setAnimatedItems(data.por_servicio.map((_, index) => index));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [data]);

  const resumen = useMemo(() => {
    if (!data) return { total_casos: 0, total_registros: 0 };
    return {
      total_casos: data.total_casos ?? 0,
      total_registros: data.total_registros ?? 0,
    };
  }, [data]);

  const porServicio = useMemo(() => {
    if (!data || !Array.isArray(data.por_servicio)) return [];
    return data.por_servicio;
  }, [data]);

  const casos = useMemo(() => {
    if (!data || !Array.isArray(data.casos)) return [];
    return data.casos;
  }, [data]);

  const openModal = (ot) => setSelectedOt(ot);
  const closeModal = () => setSelectedOt(null);

  const serviceConfigs = {
    "Aguas Domésticas": { 
      icon: "fa-tint", 
      color: "primary", 
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" 
    },
    "Aguas Industriales": { 
      icon: "fa-industry", 
      color: "info", 
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" 
    },
    "Excedentes Industriales": { 
      icon: "fa-recycle", 
      color: "success", 
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" 
    },
    "Lavado de tanques": { 
      icon: "fa-spray-can", 
      color: "warning", 
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" 
    },
    "Protección de Marca": { 
      icon: "fa-shield-alt", 
      color: "dark", 
      gradient: "linear-gradient(135deg, #a8c0ff 0%, #3f2b96 100%)" 
    },
    "Residuos Hospitalarios": { 
      icon: "fa-hospital", 
      color: "danger", 
      gradient: "linear-gradient(135deg, #ff6b6b 0%, #ffa8a8 100%)" 
    },
    "Residuos Peligrosos": { 
      icon: "fa-biohazard", 
      color: "danger", 
      gradient: "linear-gradient(135deg, #ff5858 0%, #f09819 100%)" 
    },
    "Servicios Inhouse": { 
      icon: "fa-building", 
      color: "secondary", 
      gradient: "linear-gradient(135deg, #74ebd5 0%, #9face6 100%)" 
    }
  };

  const getServiceConfig = (servicioName) => {
    return serviceConfigs[servicioName] || { 
      icon: "fa-cog", 
      color: "secondary", 
      gradient: "linear-gradient(135deg, #868f96 0%, #596164 100%)" 
    };
  };

  return (
    <Container fluid className="px-0">
      <div >
        <Card className="modern-card">
          <CardHeader className="modern-card-header">
            <div className="d-flex align-items-center justify-content-between">
              <div className="header-content">
                <h3 className="modern-title">Servicios Adicionales</h3>
                <p className="modern-subtitle">Gestión de servicios especializados</p>
              </div>
              {loading && (
                <div className="loading-indicator">
                  <Spinner size="sm" className="me-2" />
                  <span>Cargando…</span>
                </div>
              )}
            </div>
          </CardHeader>

          <CardBody className="modern-card-body">
            {!data && !loading ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fas fa-inbox"></i>
                </div>
                <h4>No hay datos disponibles</h4>
                <p>No se encontraron registros de servicios adicionales.</p>
              </div>
            ) : (
              <>

                <Row className="g-4 content-row">
                  {/* Servicios - Diseño Moderno */}
                  <Col xs={12} lg={6}>
                    <div className="section-card">
                      <div className="section-header">
                        <div className="section-title">
                          <div className="title-icon">
                            <i className="fas fa-layer-group"></i>
                          </div>
                          <div>
                            <h4>Servicios Especializados</h4>
                            <p>Distribución por tipo de servicio</p>
                          </div>
                        </div>
                        <Badge className="section-badge">
                          {porServicio.length} Servicios
                        </Badge>
                      </div>
                      
                      {porServicio.length === 0 ? (
                        <div className="empty-section">
                          <i className="fas fa-folder-open"></i>
                          <p>No hay servicios registrados</p>
                        </div>
                      ) : (
                        <div className="services-grid">
                          {porServicio.map((item, idx) => {
                            const config = getServiceConfig(item.servicio);
                            const isAnimated = animatedItems.includes(idx);
                            
                            return (
                              <div 
                                key={`${item.servicio}-${idx}`}
                                className={`service-item ${isAnimated ? 'animated' : ''}`}
                                style={{ animationDelay: `${idx * 0.1}s` }}
                              >
                                <div 
                                  className="service-icon"
                                  style={{ background: config.gradient }}
                                >
                                  <i className={`fas ${config.icon}`}></i>
                                </div>
                                
                                <div className="service-content">
                                  <h6>{item.servicio}</h6>
                                  <span className="service-subtitle">Servicio especializado</span>
                                </div>
                                
                                <div className="service-badge">
                                  <span className="badge-value">{item.casos}</span>
                                  <span className="badge-label">casos</span>
                                </div>
                                
                                <div className="service-glow"></div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Órdenes de Trabajo - Diseño Moderno */}
                  <Col xs={12} lg={6}>
                    <div className="section-card">
                      <div className="section-header">
                        <div className="section-title">
                          <div className="title-icon">
                            <i className="fas fa-clipboard-check"></i>
                          </div>
                          <div>
                            <h4>Órdenes de Trabajo</h4>
                            <p>Gestión de OTs activas</p>
                          </div>
                        </div>
                        <Badge className="section-badge success">
                          {casos.length} OTs
                        </Badge>
                      </div>

                      {casos.length === 0 ? (
                        <div className="empty-section">
                          <i className="fas fa-file-alt"></i>
                          <p>No hay OTs registradas</p>
                        </div>
                      ) : (
                        <div className="ot-list">
                          {casos.map((ot, index) => (
                            <div 
                              key={ot.id}
                              className="ot-item"
                              style={{
                                animationDelay: `${index * 0.1}s`
                              }}
                            >
                              <div className="ot-content">
                                <div className="ot-header">
                                  <Badge className="ot-number">
                                    #{(index + 1).toString().padStart(2, '0')}
                                  </Badge>
                                  <div className="ot-title">
                                    <h6>{ot.name ?? `OT ${ot.id}`}</h6>
                                    <span className="ot-id">
                                      <i className="fas fa-hashtag"></i>
                                      ID: {ot.id}
                                    </span>
                                  </div>
                                </div>
                                
                                {Array.isArray(ot.servicios) && ot.servicios.length > 0 && (
                                  <div className="ot-services">
                                    {ot.servicios.slice(0, 3).map((srv, i) => {
                                      const srvConfig = getServiceConfig(srv);
                                      return (
                                        <span 
                                          key={`${ot.id}-srv-${i}`}
                                          className="service-tag"
                                          style={{ 
                                            borderLeft: `3px solid ${srvConfig.gradient.split(' ')[4]}` 
                                          }}
                                        >
                                          <i className={`fas ${srvConfig.icon}`}></i>
                                          {srv}
                                        </span>
                                      );
                                    })}
                                    {ot.servicios.length > 3 && (
                                      <span className="service-more">
                                        +{ot.servicios.length - 3}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <Button 
                                className="btn bg-primary"
                                onClick={() => openModal(ot)}
                              >
                                <i className="fas fa-eye"></i>
                                Detalles
                              </Button>
                              
                              <div className="ot-glow"></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </CardBody>
        </Card>

        {/* Modal Moderno */}
        <Modal 
          isOpen={!!selectedOt} 
          toggle={closeModal} 
          size="lg" 
          centered
          className="modern-modal"
        >
          <ModalHeader toggle={closeModal} className="modal-header">
            <div className="modal-title-content">
              <div className="modal-icon">
                <i className="fas fa-file-invoice"></i>
              </div>
              <div>
                <h5>{selectedOt?.name ?? (selectedOt ? `OT ${selectedOt.id}` : "Detalle de OT")}</h5>
                <span>Información detallada de la orden de trabajo</span>
              </div>
            </div>
          </ModalHeader>
          
          <ModalBody className="modal-body">
            {selectedOt && (
              <>
                <Row className="modal-stats">
                  <Col md={6}>
                    <div className="modal-stat">
                      <div className="modal-stat-icon">
                        <i className="fas fa-hashtag"></i>
                      </div>
                      <div className="modal-stat-content">
                        <h4>{selectedOt.id}</h4>
                        <p>Identificador único</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="modal-stat">
                      <div className="modal-stat-icon">
                        <i className="fas fa-tag"></i>
                      </div>
                      <div className="modal-stat-content">
                        <h4>{selectedOt.name ?? "Sin nombre"}</h4>
                        <p>Nombre de la orden</p>
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="modal-section">
                  <div className="modal-section-header">
                    <div className="modal-section-icon">
                      <i className="fas fa-cogs"></i>
                    </div>
                    <h5>Servicios Asociados</h5>
                  </div>
                  
                  {Array.isArray(selectedOt.servicios) && selectedOt.servicios.length > 0 ? (
                    <div className="modal-services-grid">
                      {selectedOt.servicios.map((srv, i) => {
                        const config = getServiceConfig(srv);
                        return (
                          <div key={`modal-srv-${i}`} className="modal-service-item">
                            <div 
                              className="modal-service-icon"
                              style={{ background: config.gradient }}
                            >
                              <i className={`fas ${config.icon}`}></i>
                            </div>
                            <span>{srv}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="modal-empty">
                      <i className="fas fa-times-circle"></i>
                      <p>No hay servicios asociados a esta orden</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </ModalBody>
          
          <ModalFooter className="modal-footer">
            <Link 
              to={`/casos/view/${selectedOt?.id}`} 
              className="btn btn-primary modal-button me-2"
              style={{ textDecoration: 'none' }}
            >
              <i className="fas fa-eye"></i>
              Ver Detalles de la OT
            </Link>
            <Button className="modal-button" onClick={closeModal}>
              <i className="fas fa-check"></i>
              Entendido
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </Container>
  );
};

export default ServiciosAdicionales;